import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAccess } from '../access/AccessProvider';
import { publicRoutes, freeAccessRoutes, restrictedRoutes } from '../../utils/routes';
import AccessError from '../../pages/AccessError';
import Layout from '../Layout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireApiAccess?: boolean;
}

function shouldRestrictAccess({
  isAuthenticated,
  location,
  access,
  restrictedRoutes,
}: {
  isAuthenticated: boolean;
  location: any;
  access: any;
  restrictedRoutes: any[];
}) {
  // Special case - disable raw file access
  if (location.pathname === '/browse') {
    return true;
  }

  // Find the restricted route config for the current path
  const restricted = restrictedRoutes.find((r) => r.route === location.pathname);
  const isSubscriber =
    access?.role === 'user:paid' ||
    (access?.role === 'user:free' && access?.is_in_trial) ||
    access?.role === 'user:base';
  const isUnrestrictedRole = access?.role === 'user:developer' || access?.role === 'user:legacy';

  if (!isAuthenticated || !restricted) return false;

  const lacksRequiredPermission =
    restricted.permission &&
    (!access?.permissions || !access.permissions.has(restricted.permission));

  const needsSubscription =
    access?.features?.has('billing_subscription') && !isUnrestrictedRole && !isSubscriber;

  return needsSubscription || lacksRequiredPermission;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isPermissionsLoaded } = useAuth();
  const { access, isLoading: isAccessLoading, error: isAccessErrored } = useAccess();
  const location = useLocation();

  if (isLoading || isAccessLoading || !isPermissionsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  if (isAccessErrored) {
    return (
      <Layout>
        <AccessError />
      </Layout>
    );
  }

  if (
    isAuthenticated &&
    !isLoading &&
    !isAccessLoading &&
    access &&
    location.pathname === '/payment-required' &&
    ['user:legacy', 'user:paid', 'user:base'].includes(access.role)
  ) {
    return <Navigate to="/overview" replace />;
  }

  if (!isAuthenticated && !publicRoutes.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  // Redirect waitlist users to payment collection if paywall_access is enabled
  // But allow legacy users to bypass this check
  if (
    isAuthenticated &&
    access?.role === 'user:waitlist' &&
    access?.features.has('paywall_access') &&
    location.pathname !== '/payment-required'
  ) {
    return <Navigate to="/payment-required" replace />;
  }

  // Redirect waitlist users to the waitlist page if the self_sign_up feature flag is not enabled
  if (
    isAuthenticated &&
    access?.role === 'user:waitlist' &&
    !access?.features.has('self_sign_up') &&
    !access?.features.has('paywall_access') &&
    location.pathname !== '/waitlist'
  ) {
    return <Navigate to="/waitlist" replace />;
  }

  // Restrict access to certain routes if the user does not have the required permission or is not a subscriber
  if (
    shouldRestrictAccess({
      isAuthenticated,
      location,
      access,
      restrictedRoutes,
    })
  ) {
    return <Navigate to="/account?tab=subscription" replace />;
  }

  // Allow free access routes for authenticated users
  if (isAuthenticated && freeAccessRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  return <>{children}</>;
}
